<template>
  <div class="page-header">
    <!-- Top utility bar -->
    <div class="header-top">
      <v-container class="header-inner">
        <div class="header-top-row">
          <a
            id="email-link"
            href="mailto:help@improve.online?subject=Customer%20Inquiry&body=Hello%20Improve%20Team,%0A%0AI%20would%20like%20to%20inquire%20about%20..."
            class="header-email"
          >
            <img
              src="/static/icons/email-improve-microlearning-platform.png"
              alt=""
              class="header-email-icon"
            />
            <span>help@improve.online</span>
          </a>

          <div class="header-top-right">
            <div id="top-header" class="header-top-links">
              <SearchInput :fontSize="13" />
              <span class="top-sep">|</span>
              <a href="/about-us">Our Story</a>
              <span class="top-sep">|</span>
              <a href="/partner-programme">Partners</a>
              <span class="top-sep">|</span>
              <a href="/resources">Resources</a>
            </div>
            <a class="login-pill login-pill--teal" :href="loginHref">Login</a>
            <a class="login-pill login-pill--navy" :href="partnerUrl">Partner Login</a>
          </div>
        </div>
      </v-container>
    </div>

    <!-- Main bar -->
    <div class="header-bar">
      <v-container class="header-inner">
        <div class="header-bar-row">
          <a href="/homepage" class="brand-link">
            <img src="/static/logo.png" alt="IMPROVE" class="brand-logo" />
          </a>

          <!-- Desktop nav: only when there is room -->
          <nav class="primary-nav" aria-label="Primary">
            <router-link to="/solutions">Solutions</router-link>
            <router-link to="/features">Platform</router-link>
            <router-link to="/course-library">Library</router-link>
            <router-link to="/pricing">Pricing</router-link>
            <router-link to="/customer-stories">Stories</router-link>

            <div
              class="more-wrap"
              @mouseenter="openMore"
              @mouseleave="scheduleCloseMore"
            >
              <button
                type="button"
                class="more-btn"
                :aria-expanded="String(moreOpen)"
                @click.stop="toggleMore"
              >
                More <span aria-hidden="true">▾</span>
              </button>
              <div
                v-show="moreOpen"
                class="more-menu"
                @mouseenter="openMore"
                @mouseleave="scheduleCloseMore"
              >
                <router-link to="/leadership-plus" @click.native="closeMore">Leadership Plus</router-link>
                <router-link to="/compare" @click.native="closeMore">Compare IMPROVE</router-link>
                <router-link to="/why-switch" @click.native="closeMore">Why Switch</router-link>
                <router-link to="/contact" @click.native="closeMore">Contact</router-link>
                <router-link to="/faq" @click.native="closeMore">FAQs</router-link>
              </div>
            </div>

            <router-link to="/watch-demo" class="nav-demo">Watch Demo</router-link>

            <a :href="experienceHref" class="btn-cta btn-cta--teal" rel="noopener">Experience IMPROVE</a>
            <router-link to="/get-started" class="btn-cta btn-cta--navy">Get Started</router-link>
          </nav>

          <!-- Compact controls -->
          <div class="compact-nav">
            <router-link to="/get-started" class="btn-cta btn-cta--navy btn-cta--sm">Get Started</router-link>
            <button
              type="button"
              class="burger"
              :aria-expanded="String(mobileOpen)"
              aria-label="Open menu"
              @click="mobileOpen = !mobileOpen"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </v-container>

      <div v-if="mobileOpen" class="mobile-drawer">
        <v-container class="header-inner">
          <ul class="mobile-list">
            <li><SearchInput :fontSize="15" /></li>
            <li><a href="/solutions" @click="closeMobile">Solutions</a></li>
            <li><a href="/features" @click="closeMobile">How IMPROVE Helps</a></li>
            <li><a href="/course-library" @click="closeMobile">Learning Library</a></li>
            <li><a href="/leadership-plus" @click="closeMobile">Leadership Plus</a></li>
            <li><a href="/compare" @click="closeMobile">Compare</a></li>
            <li><a href="/pricing" @click="closeMobile">Pricing</a></li>
            <li><a href="/customer-stories" @click="closeMobile">Customer Stories</a></li>
            <li><a href="/partner-programme" @click="closeMobile">Partners</a></li>
            <li><a href="/resources" @click="closeMobile">Resources</a></li>
            <li><a href="/about-us" @click="closeMobile">Our Story</a></li>
            <li><a href="/contact" @click="closeMobile">Contact</a></li>
            <li><a href="/watch-demo" @click="closeMobile">Watch the Demo</a></li>
            <li><a :href="experienceHref" @click="closeMobile" rel="noopener">Experience IMPROVE</a></li>
            <li><a href="/get-started" @click="closeMobile">Get Started</a></li>
          </ul>
        </v-container>
      </div>
    </div>

    <div class="benefits-bar">
      <v-container class="header-inner">
        <div class="benefits-row">
          <div class="benefit-item">
            <img src="/static/icons/benefits/improve-people-development-solution.png" alt="" />
            <span>24/7 ACCESS TO COURSES</span>
          </div>
          <div class="benefit-item">
            <img src="/static/icons/benefits/integration-support-icon.png" alt="" />
            <span>HR APP INTEGRATIONS</span>
          </div>
          <div class="benefit-item">
            <img src="/static/icons/benefits/induction-training-software-for-businesses.png" alt="" />
            <span>14 DAY FREE TRIAL</span>
          </div>
          <div class="benefit-item">
            <img src="/static/icons/benefits/improves-microlearning-courses.png" alt="" />
            <span>120+ MICROLEARNING COURSES</span>
          </div>
          <div class="benefit-item benefit-item--last">
            <img src="/static/icons/benefits/compliance-management-for-businesses.png" alt="" />
            <span>COMPLIANCE MANAGEMENT</span>
          </div>
        </div>
      </v-container>
    </div>
  </div>
</template>

<script>
import { IMPROVE_CONFIG } from '@/config.js';
import { getExperienceHref, getLoginHref } from '@/config/ctas';
import SearchInput from '@/components/SearchInput.vue';

export default {
  name: 'AppHeader',
  components: { SearchInput },
  data() {
    return {
      partnerUrl: IMPROVE_CONFIG.PARTNER_URL,
      moreOpen: false,
      mobileOpen: false,
      moreCloseTimer: null,
    };
  },
  computed: {
    experienceHref() {
      return getExperienceHref();
    },
    loginHref() {
      return getLoginHref();
    },
  },
  watch: {
    $route() {
      this.closeMore();
      this.mobileOpen = false;
    },
  },
  beforeDestroy() {
    this.clearMoreTimer();
    document.removeEventListener('click', this.onDocumentClick);
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick);
  },
  methods: {
    closeMobile() {
      this.mobileOpen = false;
    },
    clearMoreTimer() {
      if (this.moreCloseTimer) {
        clearTimeout(this.moreCloseTimer);
        this.moreCloseTimer = null;
      }
    },
    openMore() {
      this.clearMoreTimer();
      this.moreOpen = true;
    },
    scheduleCloseMore() {
      this.clearMoreTimer();
      this.moreCloseTimer = setTimeout(() => {
        this.moreOpen = false;
        this.moreCloseTimer = null;
      }, 180);
    },
    toggleMore() {
      this.clearMoreTimer();
      this.moreOpen = !this.moreOpen;
    },
    closeMore() {
      this.clearMoreTimer();
      this.moreOpen = false;
    },
    onDocumentClick(e) {
      const wrap = this.$el && this.$el.querySelector('.more-wrap');
      if (this.moreOpen && wrap && !wrap.contains(e.target)) {
        this.closeMore();
      }
    },
  },
};
</script>

<style scoped>
/* Beat old global .page-header { position:fixed } without colliding with page content */
.page-header {
  position: sticky !important;
  top: 0;
  z-index: 100;
  width: 100%;
  background: #fff;
  font-family: 'Sora', sans-serif;
  box-shadow: 0 1px 0 #e6ebf1;
}

.header-inner {
  max-width: 1420px;
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.header-top {
  border-bottom: 1px solid #eef1f5;
}

.header-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  gap: 12px;
}

.header-email {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #002458;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
}

.header-email-icon {
  width: 24px;
  height: 16px;
  object-fit: contain;
}

.header-top-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-top-links {
  display: flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  font-size: 13px;
}

.header-top-links a {
  color: #334155;
  text-decoration: none;
  font-weight: 500;
  padding: 0 4px;
}

.top-sep {
  color: #cbd5e1;
}

.login-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  color: #fff !important;
}

.login-pill--teal {
  background: #08a0a8;
}

.login-pill--navy {
  background: #002458;
}

.header-bar {
  position: relative;
  background: #fff;
}

.header-bar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-height: 64px;
}

.brand-link {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  text-decoration: none;
}

/* Do not use global .logo class — it forces 180px width + padding */
.brand-logo {
  display: block;
  height: 48px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  padding: 0;
}

.primary-nav {
  display: none;
  align-items: center;
  justify-content: flex-end;
  flex: 1 1 auto;
  gap: 4px;
  min-width: 0;
}

.primary-nav > a,
.more-btn {
  flex: 0 0 auto;
  color: #002458;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 9px;
  white-space: nowrap;
  border: 0;
  background: transparent;
  border-bottom: 2px solid transparent;
  font-family: inherit;
  cursor: pointer;
  line-height: 1.2;
}

.primary-nav > a:hover,
.primary-nav > a.router-link-active,
.more-btn:hover {
  border-bottom-color: #08a0a8;
  color: #002458;
}

.nav-demo {
  color: #08a0a8 !important;
}

.more-wrap {
  position: relative;
  /* Bridge the gap so moving from "More" into the menu doesn't close it */
  padding-bottom: 14px;
  margin-bottom: -14px;
}

.more-menu {
  position: absolute;
  top: calc(100% - 10px);
  right: 0;
  min-width: 200px;
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(0, 36, 88, 0.12);
  padding: 8px 0;
  z-index: 120;
  display: flex;
  flex-direction: column;
}

.more-menu::before {
  content: '';
  position: absolute;
  top: -14px;
  left: 0;
  right: 0;
  height: 14px;
}

.more-menu a {
  display: block;
  padding: 10px 16px;
  color: #002458;
  text-decoration: none;
  font-size: 13px;
  font-weight: 600;
  text-align: left;
}

.more-menu a:hover {
  background: #eef8f8;
  color: #08a0a8;
}

.btn-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  margin-left: 4px;
}

.btn-cta--teal {
  background: #08a0a8 !important;
  color: #fff !important;
}

.btn-cta--navy {
  background: #002458 !important;
  color: #fff !important;
}

.btn-cta--sm {
  height: 34px;
  font-size: 12px;
  margin-left: 0;
}

.compact-nav {
  display: flex;
  align-items: center;
  gap: 10px;
}

.burger {
  width: 42px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: #08a0a8;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  cursor: pointer;
  padding: 0;
}

.burger span {
  display: block;
  width: 18px;
  height: 2px;
  background: #fff;
  border-radius: 1px;
}

.mobile-drawer {
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  background: #fff;
  border-bottom: 1px solid #e6ebf1;
  box-shadow: 0 16px 32px rgba(0, 36, 88, 0.12);
  z-index: 110;
  max-height: min(70vh, 520px);
  overflow: auto;
}

.mobile-list {
  list-style: none;
  margin: 0;
  padding: 8px 0 16px;
}

.mobile-list a {
  display: block;
  padding: 12px 4px;
  color: #002458;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  border-bottom: 1px solid #f1f5f9;
}

.mobile-list a:hover {
  color: #08a0a8;
}

.benefits-bar {
  background: #f5f5f5;
  border-top: 1px solid #eef1f5;
}

.benefits-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 40px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 0;
  justify-content: center;
}

.benefit-item:first-child {
  justify-content: flex-start;
}

.benefit-item--last {
  justify-content: flex-end;
}

.benefit-item img {
  height: 14px;
  width: auto;
}

.benefit-item span {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #676767;
  white-space: nowrap;
}

/* Wide desktop: show full primary nav, hide burger */
@media (min-width: 1280px) {
  .primary-nav {
    display: flex;
  }

  .compact-nav {
    display: none;
  }
}

@media (max-width: 1279px) {
  .primary-nav {
    display: none !important;
  }
}

@media (max-width: 959px) {
  #top-header,
  #email-link {
    display: none !important;
  }

  .benefit-item span {
    font-size: 8px;
  }

  .benefit-item:nth-child(4),
  .benefit-item:nth-child(5) {
    display: none;
  }
}

@media (max-width: 599px) {
  .benefits-bar {
    display: none;
  }

  .login-pill--navy {
    display: none;
  }

  .brand-logo {
    height: 40px;
  }

  .header-bar-row {
    min-height: 56px;
  }
}
</style>
